import React, { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./Reveal";
import { CONTACT } from "../../data";
import { useLang } from "../../i18n";

const NAV_IDS = [
  ["programs", "programs"],
  ["classifications", "classifications"],
  ["pricing", "pricing"],
  ["reviews", "reviews"],
  ["gallery", "gallery"],
  ["instructor", "instructor"],
];

const LangToggle = ({ lang, setLang, testidPrefix = "" }) => (
  <div
    className="inline-flex items-center rounded-full border border-white/15 p-0.5 bg-white/5"
    data-testid={`${testidPrefix}lang-toggle`}
  >
    {["en", "es"].map((l) => (
      <button
        key={l}
        data-testid={`${testidPrefix}lang-${l}`}
        onClick={() => setLang(l)}
        className={`px-2.5 py-1 rounded-full text-xs font-head font-bold uppercase tracking-wide transition-colors ${
          lang === l ? "bg-amber-400 text-[#060c1f]" : "text-slate-300 hover:text-white"
        }`}
      >
        {l === "en" ? "EN" : "ES"}
      </button>
    ))}
  </div>
);

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#060c1f]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[86px] flex items-center justify-between">
        <button onClick={() => go("top")} data-testid="logo-home-btn">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_IDS.map(([id, key]) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => go(id)}
              className="text-sm text-slate-300 hover:text-white transition-colors font-medium relative group"
            >
              {t.nav[key]}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-amber-400 group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangToggle lang={lang} setLang={setLang} />
          </div>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="header-call-btn"
            className="hidden md:flex items-center gap-2 text-sm font-mono-x text-slate-200 hover:text-amber-400 transition-colors"
          >
            <Phone size={15} className="text-amber-400" />
            {CONTACT.phone}
          </a>
          <button
            onClick={() => go("quote")}
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex bg-amber-400 hover:bg-amber-300 text-[#060c1f] font-head font-bold uppercase tracking-wide text-sm px-5 py-2.5 rounded-md transition-colors"
          >
            {t.nav.getQuote}
          </button>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#060c1f]/98 backdrop-blur-xl border-t border-white/10 px-5 py-5 flex flex-col gap-1">
          {NAV_IDS.map(([id, key]) => (
            <button
              key={id}
              onClick={() => go(id)}
              data-testid={`mobile-nav-${id}`}
              className="text-left py-2.5 text-slate-200 font-head font-semibold uppercase tracking-wide text-lg"
            >
              {t.nav[key]}
            </button>
          ))}
          <div className="mt-3">
            <LangToggle lang={lang} setLang={setLang} testidPrefix="mobile-" />
          </div>
          <button
            onClick={() => go("quote")}
            className="mt-3 bg-amber-400 text-[#060c1f] font-head font-bold uppercase py-3 rounded-md"
          >
            {t.nav.getInstantQuote}
          </button>
        </div>
      )}
    </header>
  );
};
